import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { resources, type AppLang } from "./translations";

export type { AppLang };

export const SUPPORTED_LANGS: AppLang[] = ["en", "ar"];

export const LANG_STORAGE_KEY = "lumvio.lang";

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources,
    lng: "en", // deterministic for SSR + first client render (avoids hydration mismatch)
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGS,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
}

// Arabic ships as its own chunk: the English bundle is all SSR + first paint
// needs, so ~half the translation payload leaves the critical path.
let arPromise: Promise<void> | null = null;
export function loadLanguage(lang: AppLang): Promise<void> {
  if (lang !== "ar" || i18n.hasResourceBundle("ar", "translation")) {
    return Promise.resolve();
  }
  arPromise ??= import("./translations.ar").then(({ arResources }) => {
    i18n.addResourceBundle("ar", "translation", arResources.translation, true, true);
  });
  return arPromise;
}

export async function changeLanguage(lang: AppLang): Promise<void> {
  await loadLanguage(lang);
  await i18n.changeLanguage(lang);
}

// Detect user's preferred language after hydration.
export function detectClientLanguage(): AppLang {
  if (typeof window === "undefined") return "en";
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === "ar" || stored === "en") return stored;
  } catch {
    /* noop */
  }
  const nav = window.navigator?.language || "en";
  return nav.toLowerCase().startsWith("ar") ? "ar" : "en";
}

export function isRtl(lang: string): boolean {
  return lang.startsWith("ar");
}

export function setDocumentDir(lang: string): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
  document.documentElement.dir = isRtl(lang) ? "rtl" : "ltr";
}

export default i18n;
